import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { DollarSign, Save, RotateCcw, Info } from "lucide-react";
import { adminClient } from "./api";

const ORDER = ["junk", "drywall", "furniture", "handyman", "transport"];

function Field({ label, children, hint }) {
  return (
    <div>
      <Label className="text-[10px] font-bold uppercase tracking-widest text-zinc-600">{label}</Label>
      <div className="mt-1">{children}</div>
      {hint && <p className="text-[10px] text-zinc-400 mt-1">{hint}</p>}
    </div>
  );
}

function ServiceCard({ svc, cfg, pct, onChange }) {
  const adj = (v) => Math.round(Number(v || 0) * (1 + Number(pct || 0) / 100));
  const set = (k, v) => onChange(svc, { ...cfg, [k]: v });
  return (
    <Card data-testid={`pricing-card-${svc}`} className="p-5 border-2 border-zinc-200 space-y-4">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h3 className="font-display font-black text-lg text-[#0C2E2B]">{cfg.label}</h3>
          <p className="text-[11px] text-zinc-500">Baseline shown without a photo; guidance the AI follows with one.</p>
        </div>
        <div className="text-right shrink-0">
          <div className="text-[10px] uppercase tracking-widest text-zinc-400 font-bold">Customer sees</div>
          <div data-testid={`pricing-preview-${svc}`} className="font-display font-black text-xl text-[#0C2E2B]">${adj(cfg.base_low)}–${adj(cfg.base_high)}</div>
        </div>
      </div>
      <div className="grid grid-cols-3 gap-3">
        <Field label="Minimum">
          <Input data-testid={`pricing-${svc}-min`} type="number" min="0" step="1" value={cfg.min_price} onChange={(e) => set("min_price", e.target.value)} className="font-mono-data" />
        </Field>
        <Field label="Typical low">
          <Input data-testid={`pricing-${svc}-low`} type="number" min="0" step="1" value={cfg.base_low} onChange={(e) => set("base_low", e.target.value)} className="font-mono-data" />
        </Field>
        <Field label="Typical high">
          <Input data-testid={`pricing-${svc}-high`} type="number" min="0" step="1" value={cfg.base_high} onChange={(e) => set("base_high", e.target.value)} className="font-mono-data" />
        </Field>
      </div>
      <Field label="Typical time">
        <Input data-testid={`pricing-${svc}-time`} value={cfg.time} onChange={(e) => set("time", e.target.value)} placeholder="45-75 mins" />
      </Field>
      <Field label="Price guide for the AI" hint="Plain English. The AI must follow it when it applies, e.g. '¼ truck $150 · ½ truck $275 · full truck $500 · mattress +$40 · stairs +$25/flight'.">
        <Textarea data-testid={`pricing-${svc}-guide`} rows={3} value={cfg.price_guide} onChange={(e) => set("price_guide", e.target.value)} placeholder="¼ truck $150 · ½ truck $275 · full truck $500 · mattress +$40" />
      </Field>
    </Card>
  );
}

export default function PricingAdmin() {
  const [rules, setRules] = useState(null);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);

  const load = () => adminClient.get("/admin/pricing").then(({ data }) => { setRules(data); setDirty(false); }).catch(() => toast.error("Could not load pricing"));
  useEffect(() => { load(); }, []);

  const onService = (svc, cfg) => { setRules((r) => ({ ...r, services: { ...r.services, [svc]: cfg } })); setDirty(true); };
  const onPct = (v) => { setRules((r) => ({ ...r, adjustment_pct: v })); setDirty(true); };

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await adminClient.put("/admin/pricing", rules);
      setRules(data); setDirty(false);
      toast.success("Pricing saved — new quotes use these rules now");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  if (!rules) return <div className="text-sm text-zinc-500">Loading pricing…</div>;

  return (
    <div data-testid="pricing-admin" className="space-y-6">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="font-display font-black text-3xl text-[#0C2E2B] flex items-center gap-2"><DollarSign className="w-7 h-7" /> Quote Pricing</h1>
          <p className="text-sm text-zinc-500 mt-1">Set the numbers every instant quote must respect. Changes apply to the next quote — nothing already sent changes.</p>
        </div>
        <div className="flex gap-2">
          <Button data-testid="pricing-reset-btn" variant="outline" onClick={load} disabled={!dirty || saving} className="rounded-md"><RotateCcw className="w-4 h-4 mr-1" /> Discard</Button>
          <Button data-testid="pricing-save-btn" onClick={save} disabled={!dirty || saving} className="btn-kinetic rounded-md"><Save className="w-4 h-4 mr-1" /> {saving ? "Saving…" : "Save pricing"}</Button>
        </div>
      </div>

      <Card data-testid="pricing-adjustment-card" className="p-5 border-2 border-[#0C2E2B] bg-[#0C2E2B] text-white">
        <div className="flex flex-wrap items-center gap-6">
          <div className="flex-1 min-w-[220px]">
            <div className="font-display font-black text-lg text-[#FFDF00]">Overall adjustment</div>
            <p className="text-xs text-white/70 mt-1">Nudge every quote up or down without touching each service. +10 means all prices ×1.10.</p>
          </div>
          <div className="flex items-center gap-3">
            <input data-testid="pricing-pct-slider" type="range" min="-50" max="100" step="1" value={rules.adjustment_pct} onChange={(e) => onPct(Number(e.target.value))} className="w-48 accent-[#FFDF00]" />
            <div className="flex items-center gap-1">
              <Input data-testid="pricing-pct-input" type="number" min="-50" max="100" step="1" value={rules.adjustment_pct} onChange={(e) => onPct(Number(e.target.value))} className="w-20 font-mono-data bg-white text-[#0C2E2B] font-bold" />
              <span className="font-bold">%</span>
            </div>
          </div>
        </div>
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {ORDER.map((svc) => (
          <ServiceCard key={svc} svc={svc} cfg={rules.services[svc]} pct={rules.adjustment_pct} onChange={onService} />
        ))}
      </div>

      <div className="flex items-start gap-2 text-xs text-zinc-500 bg-zinc-50 border border-zinc-200 rounded-md p-3">
        <Info className="w-4 h-4 shrink-0 mt-px" />
        <span>The minimum is guidance, not a hard floor — the AI may quote lower for clearly tiny jobs. Need a firm number on a specific job? Open it under Bookings and use "Set firm price".</span>
      </div>
    </div>
  );
}
